import * as Yup from 'yup';
import moment from 'moment';
import apiService from 'app/bff/services/apiService';
import commonService from 'app/core/services/commonService';

/**
 * @description Regular Expression
 */
// 允許中文
const chineseCharactersRegExp = /^[\u4e00-\u9fa5]+$/;
// 允許英文
const enCharactersRegExp = /^[a-zA-Z]+$/;
// 允許英文字母、數字
const enCharactersAndNumbersMixiedRegExp = /^[a-zA-Z0-9]+$/i;
// 允許英文字母、數字、中文
const enCharactersAndNumbersChinessMixiedRegExp = /^[a-zA-Z0-9\u4e00-\u9fa5]+$/i;
// 允許數字
const numbersRegExp = /^\d+$/;
// 不允許數字
const notAllowNumbersRegExp = /^[^0-9]+$/;
// 至少一個英文小寫字母、一個英文大寫字母、一個數字
const containLowercaseAndUppercaseAndNumberRegExp = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{0,}$/;
// 連續 4 個相同的英文字母或數字
const enCharactersOrNumbersConsecutive4CharactersRegExp = /([a-zA-Z0-9])\1{3}/;
// 英文字母或數字升冪
const ascendingConsecutive4CharactersRegExp = /(abcd|bcde|cdef|defg|efgh|fghi|ghij|hijk|ijkl|jklm|klmn|lmno|mnop|nopq|opqr|pqrs|qrst|rstu|stuv|tuvw|uvwx|vwxy|wxyz|0123|1234|2345|3456|4567|5678|6789)+/;
// 英文字母或數字降冪
const descendingConsecutive4CharactersRegExp = /(dcba|edcb|fedc|gfed|hgfe|ihgf|jihg|kjih|lkji|mlkj|nmlk|onml|ponm|qpon|rqpo|srqp|tsrq|utsr|vuts|wvut|xwvu|yxwv|zyxw|3210|4321|5432|6543|7654|8765|9876)+/;
// 全形中文
const fullZhHantTWCharactersRegExp = /[\u4E00-\u9FFF]/;
// 全形英文字母、數字
const fullEnCharactersAndNumbersRegExp = /[\uFE30-\uFFA0]/;
// 英文名字格式
const enNameRegExp = /^[a-zA-Z ,-]+$/;
// 身分證字號格式 (格式一)
const personalIdRegExp1 = /[A-Z]{1}[1-2||8-9]{1}[0-9]{8}/;
// 身分證字號格式 (格式二)
const personalIdRegExp2 = /[A-Z]{1}[89]{1}[7]{1}[0-9]{7}/;
// 居留證
const personalRCRegExp = /[A-Z]{1}[A-D]{1}[0-9]{8}/;
// 電子郵件格式
const emailRegExp = /^[a-zA-Z0-9_-]+([.][a-zA-Z0-9_-]+)*@[a-zA-Z0-9_-]+([.][a-zA-Z0-9_-]+)*$/;
// 中華民國電話區碼格式
const areaCodeRegExp = /^(02|03|037|04|049|05|06|07|08|082|089|0826|0836)$/;
// 行動電話號碼格式
const cellphoneNumbersRegExp = /[0]{1}[9]{1}[0-9]{8}/;

/**
 * @description [Method] 依據「區碼」使用對應的檢核規則檢核「電話號碼」格式
 * @param telAreaCode 區碼
 * @param telNumbers 電話號碼
 */
const validateTelephoneNumbers = (telAreaCode: string, telNumbers: string) => {
  if (telAreaCode === '02') {
    // 基隆市、臺北市、新北市 (02)2&3&5~8+7D
    return !!telNumbers.match(/[2-3|5-8]{1}[\d]{7}/);
  } else if (telAreaCode === '037') {
    // 苗栗縣 (037)2~9+5D
    return !!telNumbers.match(/[2-9]{1}[\d]{5}/);
  } else if (telAreaCode === '03') {
    // 宜蘭縣 (03)9+6D , 花蓮縣 (03)8+6D , 新竹縣市 (03)5&6+6D , 桃園市 (03)2&3&4+6D
    return !!telNumbers.match(/[2-6|8-9]{1}[\d]{6}/);
  } else if (telAreaCode === '049') {
    // 南投縣 (049)2~9+6D
    return !!telNumbers.match(/[2-9]{1}[\d]{6}/);
  } else if (telAreaCode === '04') {
    // 臺中市 (04)2&3+7D , 彰化縣 (04)7&8+6D
    const isTaichung = !!telNumbers.match(/[2-3]{1}[\d]{7}/);
    const isChanghua = !!telNumbers.match(/[7-8]{1}[\d]{6}/);
    return isTaichung || isChanghua;
  } else if (telAreaCode === '07') {
    // 高雄市 (07)2~9+6D
    return !!telNumbers.match(/[2-9]{1}[\d]{6}/);
  } else if (telAreaCode === '05') {
    // 嘉義縣市 (05)2~4+6D , 雲林縣 (05)5~7+6D
    return !!telNumbers.match(/[2-4|5-7]{1}[\d]{6}/);
  } else if (telAreaCode === '06') {
    // 澎湖縣 (06)9+6D , 臺南市 (06)2&3&5~7+6D
    const isPenghu = !!telNumbers.match(/[9]{1}[\d]{6}/);
    const isTainan = !!telNumbers.match(/[2-3|5-7]{1}[\d]{6}/);
    return isPenghu || isTainan;
  } else if (telAreaCode === '0836') {
    // 馬祖 (0836)2~9+4D
    return !!telNumbers.match(/[2-9]{1}[\d]{4}/);
  } else if (telAreaCode === '0826') {
    // 烏坵 (0826)6+4D
    return !!telNumbers.match(/[6]{1}[\d]{4}/);
  } else if (telAreaCode === '089') {
    // 臺東縣 (089)2~9+5D
    return !!telNumbers.match(/[2-9]{1}[\d]{5}/);
  } else if (telAreaCode === '082') {
    // 金門 (082)2~5&7~9+5D
    return !!telNumbers.match(/[2-5|7-9]{1}[\d]{5}/);
  } else if (telAreaCode === '08') {
    // 屏東縣 (08)4&7&8+6D
    return !!telNumbers.match(/[4|7-8]{1}[\d]{6}/);
  } else {
    // 無對應到任何縣市
    return false;
  }
};

/**
 * @description [Method] 檢核信用卡卡號
 * @param cardNo 信用卡卡號
 */
const validateCreditCardNumberAsync = async (cardNo: string) => {
  const result = await apiService.postPaymentCredit({ cardNo });
  return !!result;
};

/**
 * @description [Field] 檢核「姓名」
 */
const nameSchema = Yup.string()
  .max(200, '長度不得超過 200 字元')
  .matches(notAllowNumbersRegExp, '資料格式錯誤');

/**
 * @description [Field] 檢核「英文姓名」
 */
const enNameSchema = Yup.string()
  .matches(enNameRegExp, '資料格式錯誤');

/**
 * @description [Field] 檢核「身分證字號」
 */
const personalIdSchema = Yup.string()
  .max(10, '長度不得超過 10 字元')
  .test('personalIdSchema', '資料格式錯誤', (value) => {
    const isValid = value ? (!!value.match(personalIdRegExp1) || !!value.match(personalIdRegExp2)) : true;
    return isValid;
  }).test('checkNumber', '資料格式錯誤', (value) => {
    if (!value) return false;
    const conver = 'ABCDEFGHJKLMNPQRSTUVXYWZIO';
    const weights = [1, 9, 8, 7, 6, 5, 4, 3, 2, 1, 1];
    const tempId = String(conver.indexOf(value[0]) + 10) + value.slice(1);
    const array = tempId.split('');
    const checkSum = array.reduce((pre, item, index) => {
      return +item * weights[index] + pre;
    }, 0);
    return checkSum % 10 === 0;
  });
;

/**
 * @description [Field] 檢核「出生年月日」
 */
const birthdaySchema = Yup.string()
  .test('birthdaySchema', '會員限定年齡需滿 18 歲', (value) => {
    const date = value ? commonService.convertToADDate(value) : moment().format('YYYY/MM/DD');
    const ageLimit = 18;
    const isValid = moment().diff(moment(date), 'years') >= ageLimit;
    return isValid;
  });

/**
 * @description [Field] 檢核「出生年月日」(未滿七歲)
 */
const u7BirthdaySchema = Yup.string()
  .test('u7BirthdaySchema', '年齡不得大於 7 歲', (value) => {
    const date = value ? commonService.convertToADDate(value) : moment().format('YYYY/MM/DD');
    const ageLimit = 7;
    const isValid = moment().diff(moment(date), 'years') < ageLimit;
    return isValid;
  });

/**
 * @description [Field] 檢核「電子郵件」 (檢核規則與中台相同)
 */
const emailSchema = Yup.string()
  .max(50, '長度不得超過 50 字元')
  .matches(emailRegExp, '資料格式錯誤');

/**
 * @description [Field] 檢核聯絡電話「區碼」
 */
const telAreaCodeSchema = Yup.string().test({
  name: 'telAreaCodeSchema',
  test: function (value) {
    const error = { message: '' };
    const telNumbersValue = this.parent.telNo;
    const telExtValue = this.parent.telExtNo;

    // 檢核規則：若「電話」或「分機」有值，則欄位不得為空值
    if (!value && (telNumbersValue || telExtValue)) {
      error.message = '此欄位不得為空值';
    }

    // 檢核規則：只允許輸入數字
    if (value && !value.match(numbersRegExp)) {
      error.message = '只允許輸入數字';
    }

    // 檢核規則：檢核區碼合法格式
    if (value && !value.match(areaCodeRegExp)) {
      error.message = '資料格式錯誤';
    }

    return error.message ? this.createError({ message: error.message }) : true;
  }
});

/**
 * @description [Field] 檢核聯絡電話「電話」
 */
const telNumbersSchema = Yup.string().test({
  name: 'telNumbersSchema',
  test: function (value) {
    const error = { message: '' };
    const telAreaCodeValue = this.parent.telAreaNo;
    const telExtValue = this.parent.telExtNo;

    // 檢核規則：若「區碼」或「分機」有值，則欄位不得為空值
    if (!value && (telAreaCodeValue || telExtValue)) {
      error.message = '此欄位不得為空值';
    }

    // 檢核規則：「區碼」加「電話」長度不得超過 10 字元
    if (value && telAreaCodeValue && ((telAreaCodeValue.length + value.length) > 10)) {
      error.message = '區碼加電話長度不得超過 10 字元';
    }

    // 檢核規則：只允許輸入數字
    if (value && !value.match(numbersRegExp)) {
      error.message = '只允許輸入數字';
    }

    // 檢核規則：依據「區碼」使用對應的檢核規則檢核「電話號碼」格式
    if (telAreaCodeValue && value && !validateTelephoneNumbers(telAreaCodeValue, value)) {
      error.message = '資料格式錯誤';
    }

    return error.message ? this.createError({ message: error.message }) : true;
  }
});

/**
 * @description [Field] 檢核聯絡電話「分機」
 */
const telExtSchema = Yup.string().test({
  name: 'telExtSchema',
  test: function (value) {
    const error = { message: '' };

    // 檢核規則：長度不得超過 6 字元
    if (value && value.length > 6) {
      error.message = '長度不得超過 6 字元';
    }

    // 檢核規則：只允許輸入數字
    if (value && !value.match(numbersRegExp)) {
      error.message = '只允許輸入數字';
    }

    return error.message ? this.createError({ message: error.message }) : true;
  }
});

/**
 * @description [Field] 檢核「行動電話」
 */
const cellphoneNumbersSchema = Yup.string()
  .max(10, '長度不得超過 10 字元')
  .matches(cellphoneNumbersRegExp, '資料格式錯誤');

/**
 * @description [Field] 檢核「OTP 驗證碼」
 */
const OTPCodeSchema = Yup.string()
  .max(6, '長度不得超過 6 字元')
  .matches(numbersRegExp, '只允許輸入數字');

/**
 * @description [Field] 檢核地址「巷」
 */
const addr02Schema = Yup.string()
  .max(4, '「巷」欄位長度不得超過 4 字元')
  .matches(numbersRegExp, '「巷」欄位只允許輸入半形數字');

/**
 * @description [Field] 檢核地址「弄」
 */
const addr03Schema = Yup.string()
  .max(3, '「弄」欄位長度不得超過 3 字元')
  .matches(numbersRegExp, '「弄」欄位只允許輸入半形數字');

/**
 * @description [Field] 檢核地址「號」
 */
const addr04Schema = Yup.string()
  .max(4, '「號」欄位長度不得超過 4 字元')
  .matches(numbersRegExp, '「號」欄位只允許輸入半形數字');

/**
 * @description [Field] 檢核地址「號之」
 */
const addr05Schema = Yup.string()
  .max(4, '「號之」欄位長度不得超過 4 字元')
  .matches(numbersRegExp, '「號之」欄位只允許輸入半形數字');

/**
 * @description [Field] 檢核地址「樓」
 */
const addr06Schema = Yup.string()
  .matches(numbersRegExp, '「樓」欄位只允許輸入半形數字')
  .test({
    name: 'addr06Schema',
    test: function (value) {
      const error = { message: '' };

      // 檢核規則：樓層僅能輸入 1 ~ 101 層
      if (value && !(+value > 0 && +value <= 101)) {
        error.message = '「樓」欄位僅允許輸入 1 ~ 101 層';
      }

      return error.message ? this.createError({ message: error.message }) : true;
    }
  });

/**
 * @description [Field] 檢核地址「樓之」
 */
const addr07Schema = Yup.string()
  .max(3, '「樓之」欄位長度不得超過 3 字元')
  .matches(numbersRegExp, '「樓之」欄位只允許輸入半形數字');

/**
 * @description [Field] 檢核「護照號碼」
 */
const passportSchema = Yup.string()
  .max(9, '長度不得超過 9 字元')
  .matches(enCharactersAndNumbersMixiedRegExp, '資料格式錯誤');

/**
 * @description [Field] 檢核「活期帳戶」帳號
 */
const accountNoSchema = Yup.string()
  .matches(numbersRegExp, '資料格式錯誤');

/**
 * @description [Field] 檢核「信用卡」卡號、安全碼
 */
const creditCardSchema = Yup.string()
  .matches(numbersRegExp, '資料格式錯誤');

/**
 * @description [Field] 檢核「信用卡」有效期限 (年)
 */
const creditCardExpireYYSchema = Yup.string()
  .matches(numbersRegExp, '資料格式錯誤')
  .test({
    name: 'creditCardExpireYYSchema',
    test: function (value) {
      const error = { message: '' };
      const expireMonth: string = this.parent.creditCardExpireMM;

      // 檢核規則：檢核有效期限合理性
      if ((value && value.length === 2) && (expireMonth && expireMonth.length === 2)) {
        const expireDate = Number(`20${value}${expireMonth}`);
        const currentDate = +moment().format('YYYYMM');
        if (expireDate < currentDate) {
          error.message = '請重新確認有效期限';
        }
      } else {
        error.message = '請重新確認有效期限';
      }

      return error.message ? this.createError({ message: error.message }) : true;
    }
  });

/**
 * @description [Field] 檢核「信用卡」有效期限 (月)
 */
const creditCardExpireMMSchema = Yup.string()
  .matches(numbersRegExp, '資料格式錯誤')
  .test({
    name: 'creditCardExpireMMSchema',
    test: function (value) {
      const error = { message: '' };
      const expireYear: string = this.parent.creditCardExpireYY;

      // 檢核規則：檢核有效期限合理性
      if ((expireYear && expireYear.length === 2) && (value && value.length === 2)) {
        const expireDate = Number(`20${expireYear}${value}`);
        const currentDate = +moment().format('YYYYMM');
        if (+value > 12) {
          error.message = '請重新確認月份格式';
        } else if (expireDate < currentDate) {
          error.message = '請重新確認有效期限';
        }
      } else {
        error.message = '請重新確認有效期限';
      }

      return error.message ? this.createError({ message: error.message }) : true;
    }
  });

/**
 * @description [Field] 檢核「推薦人代號」
 */
const referrerCodeSchema = Yup.string()
  .test({
    name: 'referrerCodeSchema',
    test: function (value) {
      const error = { message: '' };

      // 檢核規則：長度不得小於或者超過 8 字元。
      if (value && value.length !== 8) {
        error.message = '長度需等於 8 字元';
      }

      return error.message ? this.createError({ message: error.message }) : true;
    }
  });

export default {
  chineseCharactersRegExp,
  enCharactersRegExp,
  personalRCRegExp,
  enCharactersAndNumbersMixiedRegExp,
  enCharactersAndNumbersChinessMixiedRegExp,
  numbersRegExp,
  notAllowNumbersRegExp,
  containLowercaseAndUppercaseAndNumberRegExp,
  enCharactersOrNumbersConsecutive4CharactersRegExp,
  ascendingConsecutive4CharactersRegExp,
  descendingConsecutive4CharactersRegExp,
  fullZhHantTWCharactersRegExp,
  fullEnCharactersAndNumbersRegExp,
  enNameRegExp,
  personalIdRegExp1,
  personalIdRegExp2,
  emailRegExp,
  areaCodeRegExp,
  cellphoneNumbersRegExp,
  validateTelephoneNumbers,
  validateCreditCardNumberAsync,
  nameSchema,
  enNameSchema,
  personalIdSchema,
  birthdaySchema,
  u7BirthdaySchema,
  emailSchema,
  telAreaCodeSchema,
  telNumbersSchema,
  telExtSchema,
  cellphoneNumbersSchema,
  OTPCodeSchema,
  addr02Schema,
  addr03Schema,
  addr04Schema,
  addr05Schema,
  addr06Schema,
  addr07Schema,
  passportSchema,
  accountNoSchema,
  creditCardSchema,
  creditCardExpireYYSchema,
  creditCardExpireMMSchema,
  referrerCodeSchema
};
